<template>
  <div class="score-trend-mobile-container">
    <!-- 顶部工具栏 -->
    <div class="toolbar-section">
      <div class="page-header">
        <h3>
          <i class="el-icon-data-analysis"></i>
          积分趋势
          <span v-if="userName" class="username-tag">{{ userName }}</span>
        </h3>
      </div>
      
      <div class="time-selector">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          format="yyyy-MM-dd"
          value-format="yyyy-MM-dd"
          size="small"
          :clearable="true"
          :picker-options="{ shortcuts: dateShortcuts }"
          @change="loadChartData"
          style="width: 100%;">
        </el-date-picker>
      </div>
    </div>

    <!-- 图表卡片 -->
    <div class="chart-section" v-loading="loading">
      <div class="chart-container" ref="chartContainer"></div>
    </div>

    <!-- 无数据提示 -->
    <div v-if="!loading && noData" class="no-data-section">
      <i class="el-icon-data-line"></i>
      <p>暂无积分趋势数据</p>
      <p class="hint">请先参加比赛、订正题目<br/>或发布文章获取积分</p>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'

export default {
  name: 'ScoreTrendMobile',
  data() {
    return {
      loading: false,
      noData: false,
      chart: null,
      username: '',
      userName: '',
      dateRange: null,
      
      // 日期快捷选项
      dateShortcuts: [
        {
          text: '重置',
          onClick(picker) {
            picker.$emit('pick', null)
          }
        },
        {
          text: '最近一个月',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '最近三个月',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '最近半年',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '最近一年',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
            picker.$emit('pick', [start, end])
          }
        }
      ]
    }
  },
  mounted() {
    // 从路由参数获取用户名
    this.username = this.$route.query.username || ''
    this.userName = this.$route.query.name || ''
    
    // 初始化图表
    this.initChart()
    
    // 加载数据（默认最近一个月）
    const end = new Date()
    const start = new Date()
    start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
    // 格式化日期为 YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    this.dateRange = [formatDate(start), formatDate(end)]
    this.loadChartData()
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // 初始化图表
    initChart() {
      if (this.chart) {
        this.chart.dispose()
      }
      this.chart = echarts.init(this.$refs.chartContainer)
    },
    
    // 加载图表数据
    async loadChartData() {
      if (!this.dateRange || this.dateRange.length === 0) {
        // 重置时加载全部数据
        this.dateRange = null
      }
      
      this.loading = true
      this.noData = false
      
      try {
        const params = {
          username: this.username || undefined
        }
        
        // 如果选择了日期范围，传递具体的开始和结束日期
        if (this.dateRange && this.dateRange.length === 2) {
          params.start_date = this.dateRange[0]
          params.end_date = this.dateRange[1]
        }
        // 否则不传日期参数，后端会返回全部数据
        
        const response = await request({
          url: '/api/user/score/trend',
          method: 'get',
          params
        })
        
        if (response.code === 200) {
          const data = response.data
          
          // 检查是否有数据
          if (!data.dates || data.dates.length === 0) {
            this.noData = true
            return
          }
          
          this.renderChart(data)
        } else {
          this.$message.error(response.message || '获取数据失败')
        }
      } catch (error) {
        console.error('加载图表数据失败:', error)
        this.$message.error('加载数据失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    
    // 渲染图表（移动端优化）
    renderChart(data) {
      const option = {
        title: {
          text: '积分趋势',
          left: 'center',
          top: 5,
          textStyle: {
            fontSize: 14,
            fontWeight: 600,
            color: '#303133'
          }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999'
            }
          },
          formatter: (params) => {
            let result = `<div style="font-weight: 600; margin-bottom: 6px; font-size: 12px;">${params[0].axisValue}</div>`
            
            // 总积分（折线图）
            const totalScore = params.find(p => p.seriesName === '总积分')
            if (totalScore) {
              result += `<div style="margin-bottom: 3px; font-size: 11px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${totalScore.color};margin-right:4px;"></span>
                ${totalScore.seriesName}: <b>${totalScore.value}</b>
              </div>`
            }
            
            // 增量详情
            result += `<div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #e4e7ed; font-size: 11px;">日增量：</div>`
            
            const deltas = params.filter(p => p.seriesName !== '总积分')
            let totalDelta = 0
            
            deltas.forEach(item => {
              if (item.value !== 0) {
                totalDelta += item.value
                const shortName = item.seriesName.replace('积分', '')
                result += `<div style="margin-top: 2px; font-size: 10px;">
                  <span style="display:inline-block;width:8px;height:8px;background:${item.color};margin-right:4px;"></span>
                  ${shortName}: <b style="color: ${item.value > 0 ? '#67C23A' : '#F56C6C'};">${item.value > 0 ? '+' : ''}${item.value}</b>
                </div>`
              }
            })
            
            result += `<div style="margin-top: 5px; padding-top: 5px; border-top: 1px solid #e4e7ed; font-weight: 600; font-size: 11px;">
              总增量: <b style="color: ${totalDelta > 0 ? '#67C23A' : (totalDelta < 0 ? '#F56C6C' : '#909399')};">${totalDelta > 0 ? '+' : ''}${totalDelta}</b>
            </div>`
            
            return result
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e4e7ed',
          borderWidth: 1,
          padding: 8,
          textStyle: {
            color: '#606266',
            fontSize: 11
          },
          position: function(point, params, dom, rect, size) {
            // 移动端优化tooltip位置
            const x = point[0] < size.viewSize[0] / 2 ? point[0] + 20 : point[0] - size.contentSize[0] - 20
            return [x, '20%']
          }
        },
        legend: {
          data: ['总积分', '参赛', '题目', '排名', '订正', '复盘', '文章'],
          top: 30,
          left: 'center',
          itemGap: 8,
          itemWidth: 12,
          itemHeight: 12,
          textStyle: {
            fontSize: 10
          }
        },
        grid: {
          left: '12%',
          right: '12%',
          top: '80px',
          bottom: '50px',
          containLabel: false
        },
        xAxis: {
          type: 'category',
          data: data.dates.map(date => date.substring(5)),
          axisLabel: {
            fontSize: 9,
            color: '#606266',
            rotate: 45,
            interval: Math.floor(data.dates.length / 8) || 0  // 移动端显示更少的标签
          },
          axisLine: {
            lineStyle: {
              color: '#e4e7ed'
            }
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '总积分',
            nameTextStyle: {
              fontSize: 10
            },
            position: 'left',
            axisLabel: {
              formatter: '{value}',
              fontSize: 9,
              color: '#606266'
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: '#409EFF'
              }
            },
            splitLine: {
              lineStyle: {
                color: '#f0f0f0'
              }
            }
          },
          {
            type: 'value',
            name: '增量',
            nameTextStyle: {
              fontSize: 10
            },
            position: 'right',
            axisLabel: {
              formatter: '{value}',
              fontSize: 9,
              color: '#606266'
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: '#67C23A'
              }
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '总积分',
            type: 'line',
            yAxisIndex: 0,
            data: data.total_scores,
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            lineStyle: {
              width: 2,
              color: '#409EFF'
            },
            itemStyle: {
              color: '#409EFF',
              borderWidth: 1,
              borderColor: '#fff'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.25)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
              ])
            }
          },
          {
            name: '参赛',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.game_score_deltas,
            itemStyle: {
              color: '#E6A23C'
            }
          },
          {
            name: '题目',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.question_score_deltas,
            itemStyle: {
              color: '#67C23A'
            }
          },
          {
            name: '排名',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.rank_score_deltas,
            itemStyle: {
              color: '#F56C6C'
            }
          },
          {
            name: '订正',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.fix_score_deltas,
            itemStyle: {
              color: '#909399'
            }
          },
          {
            name: '复盘',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.replay_score_deltas,
            itemStyle: {
              color: '#409EFF'
            }
          },
          {
            name: '文章',
            type: 'bar',
            stack: '增量',
            yAxisIndex: 1,
            data: data.article_score_deltas,
            itemStyle: {
              color: '#9370DB'
            }
          }
        ]
      }
      
      this.chart.setOption(option, true)
    },
    
    // 窗口大小变化处理
    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.score-trend-mobile-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 12px;

  .toolbar-section {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    
    .page-header {
      margin-bottom: 12px;
      
      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        display: flex;
        align-items: center;
        
        i {
          margin-right: 6px;
          color: #409EFF;
          font-size: 18px;
        }
        
        .username-tag {
          font-size: 12px;
          color: #909399;
          font-weight: 400;
          margin-left: 6px;
        }
      }
    }
    
    .time-selector {
      width: 100%;
    }
  }

  .chart-section {
    background: #fff;
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    
    .chart-container {
      width: 100%;
      height: 350px;
    }
  }

  .no-data-section {
    background: #fff;
    border-radius: 12px;
    padding: 50px 20px;
    text-align: center;
    color: #909399;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    
    i {
      font-size: 56px;
      margin-bottom: 16px;
      display: block;
      color: #c0c4cc;
    }
    
    p {
      font-size: 14px;
      margin: 6px 0;
      
      &.hint {
        font-size: 12px;
        color: #c0c4cc;
        line-height: 1.6;
      }
    }
  }
}
</style>
